import { Axios } from "axios";
import { RequestApiRouteConfig, useRequestApi } from "./Api/RequestApi";
import { getRequestBasePath } from "./Api/routeConfig";
import { ModalProvider, PopupBody, PopupFooter, Snackbar, SnackbarType, getCenterStyles, getFluidBoxStyles, getYCenterStyles, useAbortControllers, useRerender, useTask, ExclamationTriangleIcon, InfoIcon } from "vig-ui-library";
import React, { MutableRefObject, useEffect, useRef } from "react";
import { Alert, AlertProps, Box, Button, Checkbox, CircularProgress, CssBaseline, FormControlLabel, LinearProgress, Popover, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import { RequestFormFieldDataOutput } from "./Schemas/RequestFormFieldDataOutput";
import { RequestFormSettingsSchema } from "./Schemas/RequestFormState";
import { GetRequestTypesWithTagsInputSchema } from "./Schemas/GetRequestTypesWithTagsInput";
import { RequestHelperGetPrepopInfoInputSchema } from "./Schemas/RequestHelperGetPrepopInfoInput";
import { RequestTypeDto } from "./Schemas/RequestTypeDto";
import { filterContractsBasedOnOdvetvie, getFieldsWithType } from "./Utilities/filters";
import { addIconsToFiles, getAvailableContractsPrepoped, getFromFieldDataSuccess, getRequestFieldData, Request, prepopulateFieldValues } from "./Utilities/mappers";
import { RequestDetailInputSchema } from "./Schemas/RequestDetailInput";
import { RequestFieldDto } from "./Schemas/RequestFieldDto";
import { RequestParameterDto } from "./Schemas/RequestParameterDto";
import { RequestParameterTypeEnum } from "./Enums/RequestParameterTypeEnum";
import { RequestDetailOutput } from "./Schemas/RequestDetailOutput";
import { RequestFileDto } from "./Schemas/RequestFileDto";
import { RequestHelperGetContractPrepopInfoInputSchema } from "./Schemas/RequestHelperGetContractPrepopInfoInput";
import { RequestHelperGetPartnerPrepopInfoInputSchema } from "./Schemas/RequestHelperGetPartnerPrepopInfoInput";
import { RequestTypeStepDto } from "./Schemas/RequestTypeStepDto";
import { Step } from "./Components/Step";
import { isRequestFullfilled } from "./Utilities/typeGuards";
import { utilities } from "./Utilities/file";
import { FormProvider, useForm } from "react-hook-form";
import { RequestFormContext, SaveTaskParams, SubmitTaskParams } from "./RequestFormContext";
import { IoOpenOutline, IoPencilOutline, IoSend } from "react-icons/io5";
import { InsertRequestInputSchema } from "./Schemas/InsertRequestInput";
import { handleDevelopmentError } from "./Utilities/error";
import { isCheckboxTrue } from "./Utilities/formUtils";
import { CreatedRequestOutput } from "./Schemas/CreatedRequestOutput";
import { RequestValidationErrorTypeEnum } from "./Enums/RequestValidationErrorType";
import { LoadingButton } from "@mui/lab";
import { IoIosTrash, IoMdArrowDropdown, IoMdCreate } from "react-icons/io";
import { RequestStateTypeEnum } from "./Enums/RequestStateTypeEnum";
import { RequestFormView } from "./Enums/RequestFormView";
import { SignatureMethod } from "./Enums/SignatureMethod";
import { useMagicLink } from "./Hooks/useMagicLink";
import { SignatureMethodSelectionView } from "./Components/SignatureMethodSelectionView";
import { SignatureFlowFooters } from "./Components/SignatureFlowFooters";
import { MagicLinkSentView } from "./Components/MagicLinkSentView";
import { UploadIdCardView } from "./Components/UploadIdCardView";
import { AttachmentsSection } from "./Components/AttachmentsSection";
import { IconType } from "react-icons/lib";
import { z } from "zod";
import { PrepopFieldsQueryParamsSchema } from "./Schemas/PrepopFieldsQueryParams";
import { CompanyType } from "../Enums/CompanyType";
import { TaxonomyDto } from "./Schemas/TaxonomyDto";
import { LocalizationProvider } from '@mui/x-date-pickers';
import { skSK } from "@mui/x-date-pickers/locales";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import 'dayjs/locale/sk';
import { ThemeProvider } from "../ThemeProvider";
import { RequestSourceTypeEnum } from "./Enums/RequestSourceTypeEnum";
import { downloadStreamFile } from "./Utilities/streamFileClient";
import { RequestSubmitSuccessView } from "./Components/RequestSubmitSuccessView";
import { SubmittedRequestDetailView } from "./Components/SubmittedRequestDetailView";
import { RequestFieldsRelationInputSchema } from "./Schemas/RequestFieldsRelationInput";
import { RequestFieldsRelationOutput } from "./Schemas/RequestFieldsRelationOutput";

export type RequestFormProps = {
    requestId?: string;
    requestType?: number;
    axiosInstance: Axios;
    apiRouteConfig?: RequestApiRouteConfig;
    notificationLinkAddress: string;
    isDealer: boolean;
    requestModuleUrl: string;
    companyType: CompanyType;
    onCancelRequest: () => void;
    openSnackbar: (snackbar: Snackbar) => void;
    // For popup form
    closeForm: () => void;
    // For page form
    onClose?: () => void;
    globalErrorBuilder: (title: string, description: string) => React.ReactNode;
    /// If the function is passed, the url will be updated after the request is saved.
    /// It is useful when we implement displaying the request in a separate page.
    pageSettings?: {
        updateUrl: (requestId: string) => void;
        isPageForm: boolean;
    };
    openInPage?: (requestId?: string, requestType?: number) => void;
    /// Called when request context is available (currently passes request name for both existing-request detail load and new-request type load).
    onRequestContextLoaded?: (name: string) => void;
}

// Parsovanie query parametrov z query stringu
const parseQueryParams = (schema: z.ZodSchema) => {
    const searchParams = new URLSearchParams(window.location.search);
    const rawParams = Object.fromEntries(searchParams.entries());
    const parsedParams = schema.safeParse(rawParams);
    if (!parsedParams.success) {
        console.log("Invalid query params:", parsedParams.error);
        return {};
    }
    return parsedParams.data;
};

export const RequestForm = (props: RequestFormProps) => (
    <RequestFormProviders companyType={props.companyType}>
        <RequestFormImpl {...props} />
    </RequestFormProviders>
);

const RequestFormImpl = (props: RequestFormProps) => {
    const { openModal, openConfirmModal } = ModalProvider.useModal();

    const requestApi = useRequestApi(props.axiosInstance, props.apiRouteConfig);

    const { openSnackbar } = props;

    const theme = useTheme();

    const rerender = useRerender();

    const formMethods = useForm({ mode: "all" });

    const requestTypeId = useRef(props.requestType);

    const requestId = useRef(props.requestId);

    const hasError = useRef(false);

    const formSettings = useRef(RequestFormSettingsSchema.parse({}));

    const request = useRef<Request | null>(null);

    const [view, setView] = React.useState<RequestFormView>(RequestFormView.FORM);
    const [selectedSignatureMethod, setSelectedSignatureMethod] = React.useState<SignatureMethod | null>(null);

    // Getter function for request ID - tries request.current.requestId first, falls back to requestId.current
    const getRequestId = React.useCallback((): string | undefined => {
        return request.current?.requestId ?? requestId.current ?? undefined;
    }, []);

    // Ref for onlySaveRequest to pass to magic link hook (defined later in the component)
    const onlySaveRequestRef = React.useRef<((debounced?: boolean, shouldReloadRequestData?: boolean, successHandler?: () => void, fetchFieldsRelation?: boolean) => void) | null>(null);

    // Magic link feature hook - manages all magic link state and operations
    const magicLink = useMagicLink({
        requestApi,
        request,
        requestId,
        getRequestId,
        setView,
        onlySaveRequestRef,
    });

    const setRequestId = (id?: string) => {
        requestId.current = id;
        if (isRequestFullfilled(request)) {
            request.current.requestId = id;
        }
    }

    const definition = useRef<RequestFormFieldDataOutput | null>(null);

    const { controlledCall, abortAll } = useAbortControllers();

    /// Function will call endpoint to get request type data. If there is no request type with given id, it will throw an error.
    /// Because user does not have access to the request type.
    /// Otherwise it will return the request type.
    const validateRequestTypeAvailable = async (id: number): Promise<RequestTypeDto> => {
        const input = GetRequestTypesWithTagsInputSchema.parse({ id });

        const result = await controlledCall(({ signal }) => requestApi.getAllRequestsTypes(input, { signal }));

        const requestType = result.requestTypeAndTags[0]?.requestType;

        if (!requestType) {
            throw new Error("Request type not found");
        }

        props.onRequestContextLoaded?.(requestType.name ?? "");

        return requestType;
    }

    const getSignatureMethodCount = (manualSignRequired: boolean | null, isMagicLinkAvailable: boolean | null): number => {
        let count = 0;
        if (manualSignRequired === true) count++;
        if (isMagicLinkAvailable === true) count++;
        return count;
    }

    const isFormValid = (): boolean => {
        // Check if there are any field validation errors from the endpoint
        if (request.current?.fields) {
            const hasFieldError = request.current.fields.some((field: RequestFieldDto) => field.isIdFieldInvalid === true);
            if (hasFieldError) {
                return false;
            }
        }

        // Check if there is a global error message from the endpoint
        if (formSettings.current.globalInvalidErrorMessage) {
            return false;
        }

        // Check if there is an attachment validation error
        if (formSettings.current.attachmentValidationError) {
            return false;
        }

        // Check if attachments are required and if files exist
        if (formSettings.current.attachmentsRequired) {
            const files = request.current?.files;
            if (!files || files.length === 0) {
                return false;
            }
        }

        return true;
    }

    /// This functions take and array of fields ids and values from /getDetail endpoint
    /// and map the values to the fields.
    const fillFieldsDataFromServer = (result: RequestDetailOutput, selectedRequest: Request) => {
        if (result.fieldDetails && selectedRequest.fields) {
            for (let x = 0; x < result.fieldDetails.length; x++) {
                for (let y = 0; y < selectedRequest.fields.length; y++) {
                    if (result.fieldDetails[x].requestTypeParameterId === selectedRequest.fields[y].id) {
                        const item: RequestFieldDto = selectedRequest.fields[y];
                        const valField: RequestParameterDto = result.fieldDetails[x];

                        // Item type 3, 5, 6, 7 should be a numbers and therefore these fields have to be formatted
                        const numberTypes = [
                            RequestParameterTypeEnum.NUMBER,
                            RequestParameterTypeEnum.POISTKA_ID,
                            RequestParameterTypeEnum.SKODA_ID,
                            RequestParameterTypeEnum.PARTNER_ID,
                            RequestParameterTypeEnum.VEHICLE
                        ];

                        const isNumberType = numberTypes.includes(item.type);

                        if (valField.paramValue && isNumberType && !isNaN(valField.paramValue)) {
                            const paramValue = String(valField.paramValue);

                            const containsDecimalSymbol = paramValue.indexOf(".") >= 0 || paramValue.indexOf(",") >= 0;

                            if (containsDecimalSymbol) {
                                valField.paramValue = parseFloat(valField.paramValue);
                            } else {
                                valField.paramValue = parseInt(valField.paramValue);
                            }
                        } else if (valField.paramValue && item.type === RequestParameterTypeEnum.RADIO_BUTTON) {
                            if (item.group) {
                                selectedRequest.itemsByGroup[item.group].groupValue = valField.paramValue;
                            }
                        } else if (valField.paramValue && item.type === RequestParameterTypeEnum.CHECKBOX) {
                            valField.paramValue = isCheckboxTrue(valField.paramValue);
                        }

                        selectedRequest.fields[y].paramValue = valField.paramValue;
                        break;
                    }
                }
            }
        }
    }

    const { perform: getDataForNewRequest, isRunning: getDataForNewRequestIsRunning } = useTask(async () => {
        try {
            if (!requestTypeId.current) throw new Error("Request type is not defined");

            const foundRequestType = await validateRequestTypeAvailable(requestTypeId.current);

            const result = await controlledCall(async ({ signal }: AbortController) => await getRequestFieldData(foundRequestType, null, requestApi, false, { signal }));

            const prepopInput = RequestHelperGetPrepopInfoInputSchema.parse({ requestTypeId: requestTypeId.current });
            const prepopResult = await controlledCall(async ({ signal }) => await requestApi.getRequestPrepopInfo(prepopInput, { signal }));
            prepopResult.contracts = filterContractsBasedOnOdvetvie(foundRequestType, prepopResult.contracts);

            const availableContracts = getAvailableContractsPrepoped(prepopResult);
            // pridanie query params do formu
            const queryParams = parseQueryParams(PrepopFieldsQueryParamsSchema);
            result.fields = await prepopulateFieldValues(result.fields, prepopResult, queryParams);

            const mappedRequest: Request = getFromFieldDataSuccess(
                result.result,
                result.fields,
                result.groups,
                result.sections,
                foundRequestType,
            );

            request.current = mappedRequest;

            // Sync react-hook-form values and trigger validation
            mappedRequest.fields.forEach((field: RequestFieldDto) => {
                if (field.paramValue !== undefined && field.paramValue !== null) {
                    formMethods.setValue(field.id, field.paramValue, { shouldValidate: true });
                }
            });

            // Trigger validation for all fields
            await formMethods.trigger();

            formSettings.current = {
                ...formSettings.current,
                requestHasAiDetection: result.requestHasAiDetection,
                attachmentUploadMessage: result.attachmentUploadMessage,
                disableImmediateFileUpload: result.disableImmediateFileUpload,
                requestHasTaxonomies: result.requestHasTaxonomies,
                availableContracts: availableContracts,
                availableAddresses: prepopResult.addresses,
                manualSignRequired: result.result.manualSignRequired || false,
                isMagicLinkAvailable: result.result.isMagicLinkAvailable || false,
                signatureMethodCount: getSignatureMethodCount(result.result.manualSignRequired || false, result.result.isMagicLinkAvailable || false)
            }
        } catch (err: any) {
            if (err.code === "ERR_CANCELED") return;
            hasError.current = true;
            handleDevelopmentError(err);
            openSnackbar({
                text: "Nepodarilo sa načítať dáta pre formulár žiadosti",
                type: SnackbarType.ERROR,
            });
        } finally {
            rerender();
        }
    });


    const { perform: getExistingRequestData, isRunning: getExistingRequestDataIsRunning } = useTask(async () => {
        try {
            const detailInput = RequestDetailInputSchema.parse({ requestId: requestId.current });
            const result = await controlledCall(({ signal }) => requestApi.getDetail(detailInput, { signal }));

            if (!result.requestData) throw new Error("Request data not found");

            formSettings.current = {
                ...formSettings.current,
                attachmentsRequired: result.attachmentsRequired,
                attachmentsEnabled: result.attachmentsEnabled,
                isCurrentUserRequest: result.isMy || false,
                manualSignRequired: result.manualSignRequired || false,
                isMagicLinkAvailable: result.isMagicLinkAvailable || false,
                signatureMethodCount: getSignatureMethodCount(result.manualSignRequired || false, result.isMagicLinkAvailable || false)
            }

            requestTypeId.current = result.requestData.id;

            const requestDefinition = await controlledCall(({ signal }) => getRequestFieldData(result.requestData!, requestId.current ?? null, requestApi, result.isSubmitted, { signal }));

            const prepopInput = RequestHelperGetPrepopInfoInputSchema.parse({ requestTypeId: result.requestData.id });
            const prepopResult = await controlledCall(({ signal }: AbortController) => requestApi.getRequestPrepopInfo(prepopInput, { signal }));
            prepopResult.contracts = filterContractsBasedOnOdvetvie(result.requestData, prepopResult.contracts);

            const availableContracts = getAvailableContractsPrepoped(prepopResult);

            const mappedRequest: Request = getFromFieldDataSuccess(
                requestDefinition.result,
                requestDefinition.fields,
                requestDefinition.groups,
                requestDefinition.sections,
                result.requestData,
            );
            const lastRequestStateType = (result.requestState || [])[(result.requestState || []).length - 1].requestStateTypeId;

            formSettings.current = {
                ...formSettings.current,
                availableContracts: availableContracts,
                availableAddresses: prepopResult.addresses,
                attachmentUploadMessage: requestDefinition.attachmentUploadMessage,
                disableImmediateFileUpload: requestDefinition.disableImmediateFileUpload,
                requestHasAiDetection: requestDefinition.requestHasAiDetection,
                requestHasTaxonomies: requestDefinition.requestHasTaxonomies,
                previewMode: result.isSubmitted || lastRequestStateType == RequestStateTypeEnum.ODOSLANA_NA_PODPIS || lastRequestStateType == RequestStateTypeEnum.ZIADOST_PODPISANA_RUCNE,
                sendNotification: result.shouldBeNotified
            }

            const selectedRequest: Request = { ...mappedRequest, ...result.requestData };
            selectedRequest.requestId = result.requestId;
            selectedRequest.requestState = result.requestState || [];
            selectedRequest.isSubmitted = result.isSubmitted;
            selectedRequest.stateAnswer = result.stateAnswer;
            selectedRequest.hasGeneratedDocument = result.hasGeneratedDocument;

            // If magic link signing is active, load the magic link configuration
            if (result.isMagicLinkSigningActive === true && result.requestId && !result.isSubmitted) {
                // Use the task to load config and update state (including expireAt, method, recipient)
                const magicLinkConfig = await magicLink.loadMagicLinkConfigTask.perform(result.requestId, {
                    magicLinkSendMethod: result.magicLinkSendMethod,
                    magicLinkRecipient: result.magicLinkRecipient,
                });

                if (magicLinkConfig) {
                    // Check if a magic link exists
                    if (magicLinkConfig.latestMagicLink) {
                        // Always show magic link view if link exists (component handles expired state)
                        setView(RequestFormView.MAGIC_LINK);
                    } else {
                        // No link exists yet - check if we have stored method and recipient
                        if (result.magicLinkSendMethod != null && result.magicLinkRecipient) {
                            // Redirect to ID card upload with prefilled values
                            setView(RequestFormView.UPLOAD_ID_CARD);
                        } else {
                            // No stored method/recipient - redirect to signature method selection
                            setView(RequestFormView.SIGNATURE_METHOD_SELECTION);
                        }
                    }
                }
            }

            // Fill in the values from the server
            fillFieldsDataFromServer(result, selectedRequest);

            // Sync react-hook-form values and trigger validation
            selectedRequest.fields.forEach((field: RequestFieldDto) => {
                if (field.paramValue !== undefined && field.paramValue !== null) {
                    formMethods.setValue(field.id, field.paramValue, { shouldValidate: true });
                }
            });

            // Trigger validation for all fields
            await formMethods.trigger();

            selectedRequest.categories = result.taxonomy || [];

            const foundFiles = result.files || [];

            foundFiles.forEach((file: RequestFileDto) => {
                file.fileDeferred = { promise: {} };
                file.clicked = false;
                file.uploadCompleted = true;
                file.name = file.name + '.' + file.extension;

                const tax = selectedRequest.categories.filter(function (cat: TaxonomyDto) {
                    return cat.id === file.taxonomyId;
                });

                if (tax.length > 0) {
                    file.taxonomy = tax[0];
                }
            });

            // Do this first, so that uploadCompleted is not set to false
            addIconsToFiles(foundFiles, true, true);

            selectedRequest.files = foundFiles;

            // Prepopulate vehicle list and odvetvieId
            const docRecContractId = selectedRequest.fields.filter((fld: RequestFieldDto) => {
                return fld.type === RequestParameterTypeEnum.POISTKA_ID && fld.isDocumentRecordId === true;
            });

            if (docRecContractId && docRecContractId.length > 0 && docRecContractId[0].paramValue) {
                const vehicleFields = getFieldsWithType(selectedRequest, RequestParameterTypeEnum.VEHICLE);

                const contractInput = RequestHelperGetContractPrepopInfoInputSchema.parse({
                    poistkaId: docRecContractId[0].paramValue,
                    isVehicle: vehicleFields.length > 0,
                    needClientEmail: getFieldsWithType(selectedRequest, RequestParameterTypeEnum.CLIENT_EMAIL).length > 0,
                    vehicleRowId: vehicleFields.length > 0 ? vehicleFields[0].paramValue : null,
                });

                const prepopResult = await controlledCall(({ signal }) => requestApi.getRequestContractPrepopInfo(contractInput, { signal }));

                formSettings.current = {
                    ...formSettings.current,
                    availableVehicles: prepopResult.contractVehicles,
                    odvetvieId: prepopResult.contractOdvetvieId || 0,
                    clientEmail: prepopResult.clientEmail || '',
                }
            }

            const docRecPartnerId = selectedRequest.fields.filter((fld: RequestFieldDto) => {
                return fld.type === RequestParameterTypeEnum.PARTNER_ID && fld.isDocumentRecordId === true;
            });

            if (docRecPartnerId && docRecPartnerId.length > 0 && docRecPartnerId[0].paramValue) {
                const partnerInput = RequestHelperGetPartnerPrepopInfoInputSchema.parse({
                    partnerId: docRecPartnerId[0].paramValue,
                    needClientEmail: getFieldsWithType(selectedRequest, RequestParameterTypeEnum.CLIENT_EMAIL).length > 0,
                });

                const prepopResult = await controlledCall(({ signal }) => requestApi.getRequestPartnerPrepopInfo(partnerInput, { signal }));

                formSettings.current = {
                    ...formSettings.current,
                    clientEmail: prepopResult.clientEmail || '',
                }
            }

            request.current = selectedRequest;

            const isBulkChangeOnContracts = mappedRequest.fieldsByKey['allcontracts']
                && mappedRequest.fieldsByKey['allcontracts'].paramValue === true;

            formSettings.current = {
                ...formSettings.current,
                isBulkChangeOnContracts: isBulkChangeOnContracts
            }

            props.onRequestContextLoaded?.(result.requestData?.name ?? "");

            if (selectedRequest.files.length) formSettings.current.isSuccessfulUpload = true;
            if (!result.isMy) return formSettings.current.previewMode = true;
            if (result.isSubmitted) return;

            const relationsFromDetail = result.fieldsRelation ?? null;
            if (relationsFromDetail) {
                applyFieldRelationFillEffects(relationsFromDetail);
                getRequestFieldsRelationTask.setValue(relationsFromDetail);
            }

            onlySaveRequest();
        } catch (err: any) {
            if (err.code === "ERR_CANCELED") return;
            hasError.current = true;
            handleDevelopmentError(err);
            openSnackbar({
                text: "Nepodarilo sa načítať dáta pre formulár žiadosti",
                type: SnackbarType.ERROR,
            });
        } finally {
            rerender();
        }
    });

    const getExistingRequestDataOnBackgroundTask = useTask(async () => await getExistingRequestData());

    const applyFieldRelationFillEffects = (relation: RequestFieldsRelationOutput) => {
        const currentRequest = request.current;
        if (!currentRequest) return;

        let changed = false;
        (relation.fields ?? []).forEach((effect) => {
            if (effect.fill !== true) return;

            const field = currentRequest.fieldsByKey?.[effect.key];
            if (!field) return;

            // null/undefined => reset; otherwise assign whatever the procedure sent
            const rawValue = effect.value;
            const nextValue =
                rawValue == null
                    ? null
                    : field.type === RequestParameterTypeEnum.CHECKBOX
                        ? isCheckboxTrue(rawValue)
                        : rawValue;

            if (field.type === RequestParameterTypeEnum.RADIO_BUTTON && field.group) {
                const group = currentRequest.itemsByGroup[field.group];
                // Radio groupValue is string-only; use raw procedure value (never the checkbox boolean).
                if (group) group.groupValue = rawValue ?? null;
            }

            field.paramValue = nextValue;
            formMethods.resetField(field.id, { defaultValue: nextValue });
            changed = true;
        });

        if (changed) {
            rerender();
            // Persist filled values; do not fetch relations again (would loop with this effect).
            onlySaveRequestRef.current?.(true, false, undefined, false);
        }
    }

    const getRequestFieldsRelationTask = useTask<[string], RequestFieldsRelationOutput>(async (requestId: string) => {
        try {
            const input = RequestFieldsRelationInputSchema.parse({ requestId });
            const result = await requestApi.getRequestFieldsRelation(input);
            applyFieldRelationFillEffects(result);
            return result;
        } catch (err: any) {
            if (err.code === "ERR_CANCELED") return { fields: [] };
            handleDevelopmentError(err);
            return { fields: [] };
        }
    });

    const isRunning = getDataForNewRequestIsRunning || (getExistingRequestDataIsRunning && !getExistingRequestDataOnBackgroundTask.isRunning);

    const cancelRequest = async () => {
        openConfirmModal({
            title: "Naozaj si želáte žiadosť odstrániť?",
            description: "Žiadosť bude odstránená a zmeny nebudú vykonané.",
            confirmText: "Odstrániť",
            cancelText: "Zrušiť",
            onConfirm: async () => {
                try {
                    const cancelInput = RequestDetailInputSchema.parse({ requestId: requestId.current });
                    await requestApi.cancelRequest(cancelInput);
                    props.onCancelRequest();
                    props.closeForm();
                } catch (err) {
                    handleDevelopmentError(err);
                    openSnackbar({
                        text: "Nepodarilo sa zrušiť žiadosť. Prosím kontaktujte administrátora.",
                        type: SnackbarType.ERROR,
                    });
                }
            },
            icon: <IoIosTrash size={32} color={theme.palette.error.main} />,
        });
    }

    const editRequestAfterSign = async () => {
        openConfirmModal({
            title: "Naozaj si želáte žiadosť upraviť?",
            description: "Po editácií bude potrebné žiadosť znova vytlačiť, podpísať a naskenovať.",
            confirmText: "Upraviť žiadosť",
            cancelText: "Zrušiť",
            onConfirm: async () => {
                try {
                    const editInput = RequestDetailInputSchema.parse({ requestId: requestId.current });
                    await requestApi.cancelRequestSigning(editInput);
                    await getExistingRequestData();

                } catch (err) {
                    handleDevelopmentError(err);
                    openSnackbar({
                        text: "Nepodarila sa operácia na zmenu režimu žiadosti. Prosím kontaktujte administrátora",
                        type: SnackbarType.ERROR,
                    });
                }
            },
            icon: <IoMdCreate size={32} color={theme.palette.primary.main} />,
        })
    }

    useEffect(() => {
        if (requestId.current) {
            getExistingRequestData();
        } else {
            getDataForNewRequest();
        }

        return abortAll;
    }, []);

    const submitTask = useTask(async (params: SubmitTaskParams) => {
        const { successHandler, errorHandler } = params;

        try {
            const saveInput = InsertRequestInputSchema.parse({
                requestId: requestId.current,
                requestTypeId: requestTypeId.current,
                shouldBeNotified: formSettings.current.sendNotification,
            });

            const result = await requestApi.createAndSubmitRequest(saveInput);

            if (successHandler) { successHandler(result) };

            rerender();

        } catch (err: any) {
            if (err.code === "ERR_CANCELED") return;
            if (errorHandler) errorHandler();
            handleDevelopmentError(err);
            openSnackbar({
                text: "Nepodarilo sa odoslať žiadosť. Prosím kontaktujte administrátora.",
                type: SnackbarType.ERROR,
            })
        }
    })

    const uploadInProgressNotifierTask = useTask(async () => {
        try {
            formSettings.current.isUploadInProgress = true;
            rerender();

        } catch (err: any) {
            if (err.code === "ERR_CANCELED") return;
            handleDevelopmentError(err);
        }
    });

    const uploadInCompletedNotifierTask = useTask(async () => {
        try {
            formSettings.current.isUploadInProgress = false;
            rerender();

        } catch (err: any) {
            if (err.code === "ERR_CANCELED") return;
            handleDevelopmentError(err);
        }
    });

    const saveTask = useTask(async (params: SaveTaskParams) => {
        if (!isRequestFullfilled(request)) return;

        const { successHandler, errorHandler, uploadHandler } = params;

        formSettings.current.autosaveStatus = 'saving';
        rerender();

        const currentRequest = request.current;

        const currentSettings = formSettings.current;

        /// If it is new request, after first save, we will get requestId and we will use it for next saves.
        if (!requestId.current) {
            setRequestId(currentRequest.requestId);
        }

        const inputFieldsValues: RequestParameterDto[] = currentRequest.fields.map((field: RequestFieldDto) => {
            if (field.type === RequestParameterTypeEnum.RADIO_BUTTON) {
                const group = field.group ? currentRequest.itemsByGroup[field.group] : null;
                return {
                    requestTypeParameterId: field.id,
                    paramValue: group?.groupValue || null,
                }
            }

            return {
                requestTypeParameterId: field.id,
                paramValue: field.paramValue,
            };
        });

        try {
            const saveInput = InsertRequestInputSchema.parse({
                requestId: requestId.current,
                requestTypeId: requestTypeId.current,
                parameters: inputFieldsValues,
                notificationLinkAddress: props.notificationLinkAddress,
                shouldBeNotified: currentSettings.sendNotification,
                ignoreFieldValidations: currentSettings.requestHasAiDetection && !currentSettings.isSuccessfulUpload,
            });

            const result: CreatedRequestOutput = await requestApi.saveOrUpdateRequest(saveInput);

            formSettings.current = {
                ...formSettings.current,
                globalInfoMessage: result.infoMessage || "",
                attachmentsRequired: result.attachmentsRequired,
                attachmentsEnabled: result.attachmentsEnabled
            }

            let globalInvalidErrorMessage = "";

            if (result.idValidationError) {
                let foundFieldError = false;

                if (result.requestValidationErrorType === RequestValidationErrorTypeEnum.ATTACHMENT_MISSING_ERROR) {
                    formSettings.current.attachmentValidationError = true;
                }

                // First clear the global messages, this is important, because we dont know if there are multiple group errors.
                request.current.fields.forEach((field: RequestFieldDto) => {
                    if (field.id !== result.invalidFieldId) setIdFieldError(field, true);
                });

                // Then set new validation message
                request.current.fields.forEach((field: RequestFieldDto) => {
                    if (field.id === result.invalidFieldId) {
                        field.isIdFieldInvalid = true;

                        foundFieldError = true;

                        if (result.errorMessage) field.invalidErrorMessage = result.errorMessage;
                        else field.invalidErrorMessage = "Nesprávna hodnota.";

                        field.requestValidationErrorType = result.requestValidationErrorType;

                        // Set the validation error
                        setIdFieldError(field, false);
                    }
                });

                // If no specific field has an error but there is a validation error just save it in a global param
                if (!foundFieldError) {
                    if (result.errorMessage) globalInvalidErrorMessage = result.errorMessage;
                    else globalInvalidErrorMessage = "Nie je možné odoslať formulár, prosím skontrolujte zadané údaje.";
                    formSettings.current.globalInvalidErrorMessage = globalInvalidErrorMessage;
                    rerender();
                }
            } else {
                formSettings.current.globalInvalidErrorMessage = "";
                formSettings.current.attachmentValidationError = false;
                rerender();
                if (!result.idValidationError) {
                    request.current.fields.forEach((field: RequestFieldDto) => {
                        setIdFieldError(field, true);
                        delete field.isIdFieldInvalid;
                    });
                }
                if (successHandler) successHandler();
            }

            showSuccessSaveNotification();

            if (result.requestId) {
                if (!requestId.current || requestId.current !== result.requestId) {
                    setRequestId(result.requestId);
                    if (props.pageSettings?.updateUrl) props.pageSettings.updateUrl(result.requestId);
                }
            }

            if (uploadHandler) {
                await uploadHandler();
            }

            if (params.fetchFieldsRelation && result.requestId) {
                getRequestFieldsRelationTask.perform(result.requestId);
            }
        } catch (err: any) {
            if (err.code === "ERR_CANCELED") return;
            if (errorHandler) errorHandler();
            handleDevelopmentError(err);
            openSnackbar({
                text: "Nepodarilo sa uložiť žiadosť. Prosím kontaktujte administrátora.",
                type: SnackbarType.ERROR,
            })
        }
    }, { keepLatest: true });

    const allowSubmit = (uploadProcesses: boolean[]) => {
        return new Promise<void>((resolve, reject) => {
            if (uploadProcesses && uploadProcesses.length > 0) {
                openSnackbar({ text: "Prosím najprv počkajte, kým sa ukončí nahrávanie súborov" });
                reject();
            } else {
                resolve();
            }
        });
    }

    /// Method call saveTask without any more handlers. This is used just for pure saving withou any additional actions.
    const onlySaveRequest = (
        debounced: boolean = true,
        shouldReloadRequestData: boolean = false,
        successHandler?: () => void,
        fetchFieldsRelation: boolean = false,
    ) => {
        const params: SaveTaskParams = {
            fetchFieldsRelation,
            successHandler: async () => {
                allowSubmit(formSettings.current.uploadProcesses);

                if (shouldReloadRequestData) {
                    await getExistingRequestDataOnBackgroundTask.perform();
                }
                if (successHandler) { successHandler(); }
            }
        };
        if (debounced) saveTask.debouncedPerform(params);
        else saveTask.perform(params);
    }

    // Set the ref after onlySaveRequest is defined
    onlySaveRequestRef.current = onlySaveRequest;

    /// Method call saveTask and open dialog with option to submit request.
    const saveRequestAndSubmit = async (debounced = true, ismanuallySigned: boolean | null = null) => {
        if (formSettings.current.manualSignRequired === true && ismanuallySigned !== true) {
            const params = {
                successHandler: async () => {
                    const submitParams = {
                        errorHandler: () => {
                            formSettings.current.previewMode = false;
                            rerender();
                        },
                        successHandler: async (_result: CreatedRequestOutput) => {
                            await getExistingRequestDataOnBackgroundTask.perform();
                            downloadRequestDocument();
                        }
                    }

                    await allowSubmit(formSettings.current.uploadProcesses);
                    formSettings.current.previewMode = true;
                    rerender();
                    if (debounced) submitTask.debouncedPerform(submitParams);
                    else submitTask.perform(submitParams);
                }
            };
            if (debounced) saveTask.debouncedPerform(params);
            else saveTask.perform(params);
        }
        else {
            const params = {
                successHandler: async () => {
                    await allowSubmit(formSettings.current.uploadProcesses);
                    formSettings.current.previewMode = true;
                    rerender();

                    let submitConfirmed = false;

                    openConfirmModal({
                        title: "Naozaj si želáte odoslať žiadosť?",
                        description: "Po odoslaní už nebude možné zmeniť žiadne údaje. Prosím, skontrolujte si formulár.",
                        confirmText: "Odoslať",
                        cancelText: "Zrušiť",
                        onConfirm: async () => {
                            try {
                                submitConfirmed = true;
                                const submitParams = {
                                    errorHandler: () => {
                                        formSettings.current.previewMode = false;
                                        rerender();
                                    },
                                    successHandler: async (result: CreatedRequestOutput) => {
                                        if (result.bsignUrl) {
                                            window.open(result.bsignUrl, '_blank', 'noopener,noreferrer');
                                            props.onClose?.();
                                            return;
                                        }

                                        await getExistingRequestDataOnBackgroundTask.perform();
                                        formSettings.current.previewMode = false;
                                        setView(RequestFormView.SUBMIT_SUCCESS);
                                        rerender();
                                    }
                                }

                                if (debounced) submitTask.debouncedPerform(submitParams);
                                else submitTask.perform(submitParams);

                            } catch (err) {
                                handleDevelopmentError(err);
                            }
                        }
                    }, () => {
                        if (submitConfirmed) return;
                        formSettings.current.previewMode = false;
                        rerender();
                    });
                }
            };
            if (debounced) saveTask.debouncedPerform(params);
            else saveTask.perform(params);
        }
    }

    const downloadRequestDocument = async () => {
        await allowSubmit(formSettings.current.uploadProcesses);
        try {
            const requestBasePath = getRequestBasePath(props.apiRouteConfig);
            const url = `${props.requestModuleUrl}/${requestBasePath}/streamrequestdocumentfile?id=${requestId.current}&download=true`;
            const response = await props.axiosInstance.get(url, { responseType: "blob" });
            const blob = new Blob([response.data], { type: response.headers["content-type"] });
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = downloadUrl;
            a.download = requestId.current + ".pdf";
            a.click();
        } catch (err) {
            handleDevelopmentError(err);
        }
    }

    const downloadGeneratedSubmittedRequestDocument = async () => {
        await allowSubmit(formSettings.current.uploadProcesses);
        try {
            if (!request.current) return;
            const generatedSubmittedRequestDocument = request.current.files?.find(
                (file) => file?.taxonomy?.isRequestDocument === true
            );

            if (!generatedSubmittedRequestDocument?.id) {
                return;
            }

            await downloadStreamFile(
                props.axiosInstance,
                props.requestModuleUrl,
                generatedSubmittedRequestDocument,
                props.apiRouteConfig
            );
        } catch (err) {
            handleDevelopmentError(err);
        }
    };

    const timeout = useRef<number | null>(null);

    const showSuccessSaveNotification = () => {
        if (typeof timeout.current === "number") clearTimeout(timeout.current);
        formSettings.current.autosaveStatus = 'saved';
        rerender();
        timeout.current = setTimeout(() => {
            formSettings.current.autosaveStatus = 'idle';
            rerender();
        }, 2000);
    }

    if (isRunning) return <Box sx={{ ...getFluidBoxStyles(), ...getCenterStyles() }}>
        <CircularProgress />
    </Box>

    if (!definition || !isRequestFullfilled(request)) return props.globalErrorBuilder(
        "Dáta neboli nájdené",
        "Dáta pre formulár žiadosti neboli nájdené, skontrolujte prosím zadané údaje a skúste to znova. Ak problém pretrváva, kontaktujte podporu."
    );

    if (hasError.current) return props.globalErrorBuilder(
        "Chyba pri načítaní dát",
        "Nastala chyba pri načítaní dát pre formulár žiadosti, skontrolujte prosím zadané údaje a skúste to znova. Ak problém pretrváva, kontaktujte podporu."
    );

    const setIdFieldError = (field: RequestFieldDto, shouldClearValidationError: boolean) => {
        let globalInvalidErrorMessage: React.ReactNode = "";

        if (!field.isIdFieldInvalid) return;

        if (!shouldClearValidationError) {
            if (field.requestValidationErrorType === RequestValidationErrorTypeEnum.SQL_VALIDATION_ERROR) {
                globalInvalidErrorMessage = `Pole ${field.displayTitle || field.parameterText}: ${field.invalidErrorMessage}`;
                delete field.requestValidationErrorType;
            } else if (field.type === RequestParameterTypeEnum.RADIO_BUTTON && field.group) {
                const items = request.current.itemsByGroup[field.group].children
                    .map((item) => {
                        return <li>{item.field ? item.field.parameterText || "" : item.section?.displayTitle || ""}</li>
                    });

                globalInvalidErrorMessage = <Box>
                    <span>Prosím vyberte jednu z možností:</span>
                    <ul>{items}</ul>
                </Box>
            } else if (
                field.type === RequestParameterTypeEnum.CHECKBOX
                && field.atLeastOneRequiredWithinGroup
                && !field.required
            ) {
                const groupCheckboxes = request.current.fields.filter((item: RequestFieldDto) =>
                    item.type === RequestParameterTypeEnum.CHECKBOX
                    && item.atLeastOneRequiredWithinGroup
                    && !item.required
                    && (field.group ? item.group === field.group : item.id === field.id)
                );
                const fieldNames = groupCheckboxes
                    .map((item: RequestFieldDto) => item.parameterText)
                    .filter(Boolean)
                    .join(", ");

                globalInvalidErrorMessage = fieldNames
                    ? `Vyberte aspoň jednu možnosť: ${fieldNames}`
                    : "Vyberte aspoň jednu možnosť.";
            } else {
                globalInvalidErrorMessage = field.invalidErrorMessage
                    || `Prosím skontrolujte pole ${field.displayTitle || field.parameterText}`;
            }

            formSettings.current.globalInvalidErrorMessage = globalInvalidErrorMessage;
            rerender();

            const skipFieldError = field.atLeastOneRequiredWithinGroup && !field.required;
            if (skipFieldError) return;

            try {
                const result = formMethods.setError(field.id, {
                    type: 'invalidId',
                    message: field.invalidErrorMessage || undefined,
                });
                return result;
            } catch (err) {
                handleDevelopmentError(err);
            } finally {
                rerender();
            }

            return;
        }

        try {
            const result = formMethods.clearErrors(field.id);
            formMethods.trigger(field.id);
            return result;
        } catch (err) {
            handleDevelopmentError(err);
        }
    }

    const {
        requestHasAiDetection, showFileLoading, isSuccessfulUpload,
        disableImmediateFileUpload, attachmentUploadMessage, attachmentValidationError
    } = formSettings.current;

    const stepsVisible = !requestHasAiDetection || (!showFileLoading && isSuccessfulUpload);

    const attachmentUploadMessageVisible = !disableImmediateFileUpload && !!attachmentUploadMessage && !attachmentValidationError;

    const requestFormContext: Omit<RequestFormContext, "pathToProp"> = {
        request,
        settings: formSettings,
        requestApi,
        rerenderForm: rerender,
        onlySaveRequest,
        saveRequestAndSubmit,
        downloadRequestDocument,
        downloadGeneratedSubmittedRequestDocument,
        saveTask,
        submitTask,
        isSaving: saveTask.isRunning || submitTask.isRunning,
        cancelRequest,
        editRequestAfterSign,
        requestModuleUrl: props.requestModuleUrl,
        apiRouteConfig: props.apiRouteConfig,
        openSnackbar,
        openModal,
        openConfirmModal,
        resetField: formMethods.resetField,
        axiosInstance: props.axiosInstance,
        isPageForm: props.pageSettings?.isPageForm ?? false,
        isUploadInProgress: formSettings.current.isUploadInProgress,
        uploadInProgressNotifierTask: uploadInProgressNotifierTask,
        uploadInCompletedNotifierTask: uploadInCompletedNotifierTask,
        getExistingRequestDataOnBackgroundTask,
        isFormValid,
        view,
        setView,
        selectedSignatureMethod,
        setSelectedSignatureMethod,
        magicLinkUploadMethod: magicLink.magicLinkUploadMethod,
        setMagicLinkUploadMethod: magicLink.setMagicLinkUploadMethod,
        magicLinkDeliveryMethod: magicLink.magicLinkDeliveryMethod,
        setMagicLinkDeliveryMethod: magicLink.setMagicLinkDeliveryMethod,
        magicLinkRecipient: magicLink.magicLinkRecipient,
        setMagicLinkRecipient: magicLink.setMagicLinkRecipient,
        magicLinkError: magicLink.magicLinkError,
        setMagicLinkError: magicLink.setMagicLinkError,
        magicLinkExpireAt: magicLink.magicLinkExpireAt,
        setMagicLinkExpireAt: magicLink.setMagicLinkExpireAt,
        isMagicLinkOpened: magicLink.isMagicLinkOpened,
        magicLinkEmails: magicLink.magicLinkEmails,
        setMagicLinkEmails: magicLink.setMagicLinkEmails,
        magicLinkPhones: magicLink.magicLinkPhones,
        setMagicLinkPhones: magicLink.setMagicLinkPhones,
        isIdCardUploadRequiredForRequest: magicLink.isIdCardUploadRequiredForRequest,
        idCardFrontTaxonomyId: magicLink.idCardFrontTaxonomyId,
        idCardBackTaxonomyId: magicLink.idCardBackTaxonomyId,
        getIdCardFrontFileId: magicLink.getIdCardFrontFileId,
        getIdCardBackFileId: magicLink.getIdCardBackFileId,
        loadMagicLinkConfigTask: magicLink.loadMagicLinkConfigTask,
        startMagicLinkProcessTask: magicLink.startMagicLinkProcessTask,
        cancelMagicLinkProcessTask: magicLink.cancelMagicLinkProcessTask,
        createMagicLinkTask: magicLink.createMagicLinkTask,
        getRequestFieldsRelationTask,
    }

    const openSubmittedRequestDetailView = () => {
        // Defensive: the render branch uses `request.current?.isSubmitted`.
        if (request.current) {
            request.current.isSubmitted = true;
        }
        // Must leave SUBMIT_SUCCESS, otherwise success view keeps rendering.
        setView(RequestFormView.FORM);
        rerender();
    };

    const getSteps = (): JSX.Element[] => {
        return request.current.steps.map((step: RequestTypeStepDto, index: number) => {
            return (
                <Step
                    key={index}
                    step={step}
                    {...requestFormContext}
                    pathToProp={utilities.createPathToProp("steps", index)}
                />
            )
        });
    }

    {/* -------------------------------------------------------------- */ }
    {/* Upload processes Loading */ }
    {/* -------------------------------------------------------------- */ }
    return <FormWrapper isPageForm={requestFormContext.isPageForm}>
        <PopupBody sx={{ borderBottom: "none", overflow: "unset" }}>
            {view === RequestFormView.SUBMIT_SUCCESS ? (
                <RequestSubmitSuccessView
                    {...requestFormContext}
                    companyType={props.companyType}
                    onOpenRequestDetail={openSubmittedRequestDetailView}
                    onClose={props.onClose}
                />

            ) : view === RequestFormView.SIGNATURE_METHOD_SELECTION ? (
                <SignatureMethodSelectionView {...requestFormContext} />
            ) : request.current?.isSubmitted ? (
                <SubmittedRequestDetailView {...requestFormContext} />
            ) : view === RequestFormView.MAGIC_LINK ? (
                <MagicLinkSentView {...requestFormContext} />
            ) : view === RequestFormView.UPLOAD_ID_CARD ? (
                <UploadIdCardView {...requestFormContext} />
            ) : (
                <>
                    {/* -------------------------------------------------------------- */}
                    {/* Upload processes Loading */}
                    {/* -------------------------------------------------------------- */}
                    {!props.pageSettings?.isPageForm && <Box sx={{
                        position: "sticky",
                        top: 0,
                    }}>
                        <UploadProcessesProgress {...requestFormContext} />
                    </Box>}

                    <Box sx={{ display: "flex", flexDirection: "column", position: "relative" }}>
                        {/* -------------------------------------------------------------- */}
                        {/* Request Answer Field Component Here */}
                        {/* -------------------------------------------------------------- */}
                        {request.current.stateAnswer && (
                            <Box p={2}>
                                <Tooltip title="Spätná väzba">
                                    <InfoIcon width={16} height={16} color={theme.palette.info.main} />
                                </Tooltip>
                                <Typography variant="body2">{request.current.stateAnswer}</Typography>
                            </Box>
                        )}

                        <Box>
                            {/* -------------------------------------------------------------- */}
                            {/* Form Body */}
                            {/* -------------------------------------------------------------- */}
                            <FormProvider {...formMethods}>
                                {stepsVisible && getSteps()}

                                <AttachmentsSection
                                    attachmentUploadMessageVisible={attachmentUploadMessageVisible}
                                    requestFormContext={requestFormContext}
                                />
                            </FormProvider>

                            {/* -------------------------------------------------------------- */}
                            {/* Global info message */}
                            {/* -------------------------------------------------------------- */}
                            {formSettings.current.globalInfoMessage && request.current.ismanuallySigned == null && (
                                <RequestFormAlert
                                    severity="info"
                                    text={formSettings.current.globalInfoMessage}
                                />
                            )}

                            {/* -------------------------------------------------------------- */}
                            {/* Global invalid message */}
                            {/* -------------------------------------------------------------- */}
                            {formSettings.current.globalInvalidErrorMessage && (
                                <RequestFormAlert
                                    severity="error"
                                    text={formSettings.current.globalInvalidErrorMessage}
                                />
                            )}
                        </Box>
                    </Box>
                </>
            )}
        </PopupBody>

        <PopupFooter sx={{
            backgroundColor: "transparent",
            borderTop: "none",
        }}>
            {view === RequestFormView.SUBMIT_SUCCESS || request.current?.isSubmitted ? (
                null
            ) : view === RequestFormView.SIGNATURE_METHOD_SELECTION || view === RequestFormView.UPLOAD_ID_CARD ? (
                <SignatureFlowFooters {...requestFormContext} />
            ) : view === RequestFormView.MAGIC_LINK ? (
                // MagicLinkSentView has its own footer, so we don't render anything here
                null
            ) : (
                <CTAButtons
                    {...requestFormContext}
                    isDealer={props.isDealer}
                    companyType={props.companyType}
                    manualSignRequired={formSettings.current.manualSignRequired}
                    isMagicLinkAvailable={formSettings.current.isMagicLinkAvailable}
                    closePopup={props.closeForm}
                    requestId={requestId}
                    requestType={requestTypeId}
                    isPageForm={props.pageSettings?.isPageForm ?? false}
                    openInPage={props.openInPage}
                    closeForm={props.closeForm}
                />
            )}
        </PopupFooter>
    </FormWrapper>
};

export type RequestFormProvidersProps = {
    children: React.ReactNode;
    companyType: CompanyType;
}

export const RequestFormProviders = (props: RequestFormProvidersProps) => {
    return <ThemeProvider companyType={props.companyType}>
        <CssBaseline />
        <LocalizationProvider
            dateAdapter={AdapterDayjs}
            adapterLocale="sk"
            localeText={skSK.components.MuiLocalizationProvider.defaultProps.localeText}
        >
            <ModalProvider>
                {props.children}
            </ModalProvider>
        </LocalizationProvider>
    </ThemeProvider>
}

const FormWrapper = ({ isPageForm, children }: { isPageForm: boolean; children: React.ReactNode }) => {
    if (isPageForm) {
        // Single scroll parent should be the page shell (e.g. ContentWrapper). Nested overflow:auto
        // here breaks MUI Collapse / scroll anchoring inside attachments sections.
        return (
            <Box
                className="request-form-wrapper"
                sx={{
                    height: "100%",
                    minHeight: 0,
                    paddingBottom: "80px",
                }}
            >
                {children}
            </Box>
        );
    }

    return <>{children}</>;
};

const UploadProcessesProgress = (props: Omit<RequestFormContext, "pathToProp">) => {
    const { uploadProcesses } = props.settings.current;

    return <Box sx={{ width: "100%", height: 4 }}>
        {uploadProcesses.length > 0 && <LinearProgress />}
    </Box>
}

type CTAButtonsProps = Omit<RequestFormContext, "pathToProp"> & {
    isDealer: boolean;
    companyType: CompanyType;
    manualSignRequired: boolean | null;
    isMagicLinkAvailable: boolean | null;
    closePopup: () => void;
    requestId: MutableRefObject<string | undefined>;
    requestType: MutableRefObject<number | undefined>;
    isPageForm: boolean;
    openInPage: RequestFormProps["openInPage"];
    closeForm: RequestFormProps["closeForm"];
};

const CTAButtons = (props: CTAButtonsProps) => {
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
    const onlySmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

    const { onlySaveRequest, saveRequestAndSubmit, downloadRequestDocument, getExistingRequestDataOnBackgroundTask, isSaving, request, settings, editRequestAfterSign, rerenderForm, requestId, loadMagicLinkConfigTask, openSnackbar, setSelectedSignatureMethod, setView } = props;

    const displayLoaderInButtons = isSaving || getExistingRequestDataOnBackgroundTask.isRunning || loadMagicLinkConfigTask.isRunning;

    const currentRequest = request.current;

    const isRequestSubmitDisabled = () => {
        // If request is sent for sign and manual sign is required, always enable the download button
        if (isSentForSign && props.manualSignRequired && !(props.request.current.ismanuallySigned ?? false)) {
            return false;
        }
        if (displayLoaderInButtons) return true;
        if (currentRequest && !currentRequest.requestId) return true;
        if (!props.isFormValid()) return true;
        return false;
    };

    const { previewMode } = settings.current;

    const requestsStates = currentRequest.requestState || [];

    const lastRequestState = requestsStates.length > 0 ? requestsStates[requestsStates.length - 1] : null;

    const isSentForSign = previewMode &&
        !request.current.isSubmitted &&
        lastRequestState?.requestStateTypeId === RequestStateTypeEnum.ODOSLANA_NA_PODPIS;

    const signatureMethodCount = settings.current.signatureMethodCount;
    const isNotSubmitted = !request.current.isSubmitted;
    const isClientRequest = request.current.sourceTypeId === RequestSourceTypeEnum.KLIENT;
    const isManuallySigned = props.request.current.ismanuallySigned ?? false;

    const getSubmitButtonLabel = () => {
        // If request is sent for sign and manual sign is required, show download button
        // (user has already selected manual sign, so don't show selection button)
        if (isSentForSign && props.manualSignRequired && !isManuallySigned) {
            return "Stiahnuť žiadosť na podpis";
        }

        // If we have at least 2 signature methods and request is not submitted, show selection text
        // (but only if request is not already sent for sign with manual sign)
        if (signatureMethodCount >= 2 && isNotSubmitted && !(isManuallySigned && isNotSubmitted)) {
            return "Vybrať spôsob podpisu";
        }

        if (isClientRequest) return "Odoslať žiadosť";
        if (isSentForSign && !props.manualSignRequired) return "Podpísať";
        if (props.manualSignRequired && isManuallySigned) return "Odoslať podpísanú žiadosť";
        if (props.manualSignRequired) return "Podpísať žiadosť";

        return "Odoslať žiadosť";
    }
    const shouldShowSignatureSelection = signatureMethodCount >= 2 && isNotSubmitted && !(isSentForSign && props.manualSignRequired) && !isManuallySigned;

    const continueManualSignature = async () => {
        const config = await loadMagicLinkConfigTask.perform();
        if (!config) {
            openSnackbar({ text: "Nepodarilo sa načítať požiadavky na občiansky preukaz." });
            return;
        }

        if (!config.isIdCardUploadRequiredForRequest) {
            setSelectedSignatureMethod(SignatureMethod.Manual);
            setView(RequestFormView.UPLOAD_ID_CARD);
            return;
        }

        saveRequestAndSubmit(false, isManuallySigned);
    };

    const handleSubmitButtonClick = () => {
        if (shouldShowSignatureSelection) {
            // Save the request first and validate before showing signature method selection
            onlySaveRequest(false, false, () => {
                // After save, check if form is still valid
                if (props.isFormValid()) {
                    props.setView(RequestFormView.SIGNATURE_METHOD_SELECTION);
                }
                // If not valid, error messages will be displayed from the save operation
            });
        } else if (isSentForSign && props.manualSignRequired && !isManuallySigned) {
            downloadRequestDocument();
        } else if (props.manualSignRequired && !isManuallySigned && !isClientRequest) {
            void continueManualSignature();
        } else {
            saveRequestAndSubmit(false, isManuallySigned);
        }
    };

    const submitButton = <LoadingButton
        startIcon={shouldShowSignatureSelection ? <IoPencilOutline /> : <IoSend />}
        size="large"
        variant="contained"
        color="primary"
        onClick={handleSubmitButtonClick}
        disabled={isRequestSubmitDisabled()}
        loading={displayLoaderInButtons}
        fullWidth={props.isPageForm && onlySmallScreen}
    >
        {getSubmitButtonLabel()}
    </LoadingButton>

    const sendNotificationCheckbox = settings.current.isCurrentUserRequest && !currentRequest.isSubmitted ? (
        <FormControlLabel
            control={
                <Checkbox
                    checked={settings.current.sendNotification}
                    onChange={(_, checked) => {
                        settings.current.sendNotification = checked;
                        rerenderForm();
                        const existingRequestId = requestId.current ?? request.current?.requestId;
                        if (existingRequestId) {
                            onlySaveRequest(false);
                        }
                    }}
                />
            }
            label="Chcem dostať oznámenie o vybavení mojej žiadosti"
            sx={{
                marginRight: 0,
                userSelect: "none",
                maxWidth: "unset",
                "& .MuiFormControlLabel-label": {
                    fontFamily: theme.typography.fontFamily,
                    fontWeight: theme.typography.fontWeightRegular,
                    fontSize: theme.typography.pxToRem(15),
                    lineHeight: 1.48,
                    letterSpacing: 0,
                    color: theme.palette.grey[800],
                },
            }}
        />
    ) : null;

    const submitButtonWithNotification = (
        <Box
            sx={{
                display: "flex",
                flexDirection: props.isPageForm && onlySmallScreen ? "column" : "row",
                alignItems: props.isPageForm && onlySmallScreen ? "stretch" : "center",
                gap: 2.5,
                flexWrap: "wrap",
                justifyContent: "flex-end",
                width: props.isPageForm && onlySmallScreen ? "100%" : "auto",
            }}
        >
            {sendNotificationCheckbox}
            {submitButton}
        </Box>
    );

    const showSubmitSentForSignOrSigned =
        (isSentForSign || props.request.current.ismanuallySigned == true) && !currentRequest.isSubmitted;
    const showSubmitDraftFlow = !previewMode || displayLoaderInButtons;

    type Action = {
        label: string;
        icon: IconType;
        handler: () => void;
        visible: boolean;
        color?: string;
    }

    const actions: Action[] = [
        {
            label: "Otvoriť na celej obrazovke",
            icon: IoOpenOutline,
            handler: () => {
                props.closeForm();
                if (props.openInPage) props.openInPage(props.requestId.current, props.requestType.current);
            },
            visible: !props.isPageForm && !!props.openInPage,
        },
    ];

    if (settings.current.isCurrentUserRequest) {
        // TODO NPM PACKAGE: IN NEW FIGMA THESE BUTTONS ARE DISPLAYED WE SHOULD DISCUSS IT IN FURTHER DISCUSSION
        // actions.push({
        //     label: "Odstrániť žiadosť",
        //     icon: IoIosTrash,
        //     handler: cancelRequest,
        //     visible: !!currentRequest.requestId && !currentRequest.isSubmitted && !displayLoaderInButtons,
        //     color: theme.palette.error.main,
        // });

        if ((isSentForSign && props.manualSignRequired) || (props.manualSignRequired && props.request.current.ismanuallySigned == true)) {
            actions.push({
                label: "Upraviť žiadosť",
                icon: IoMdCreate,
                handler: editRequestAfterSign,
                visible: !!currentRequest.requestId && !currentRequest.isSubmitted && !displayLoaderInButtons,
                color: theme.palette.primary.main,
            });
        }
    }

    const moreOptionsVisible = actions.filter((action) => action.visible).length > 1;

    const closePopupButton = <Button size="large" variant="contained" color="secondary" onClick={(e) => {
        e.stopPropagation();
        props.closePopup();
    }}>
        Zatvoriť
    </Button>;

    return <Box sx={{ ...getCenterStyles(), gap: 1, flexWrap: "wrap", justifyContent: "flex-end", width: "100%", marginTop: "40px" }}>

        {moreOptionsVisible && (
            <>
                <Button size="large" variant="contained" onClick={(e) => setAnchorEl(e.currentTarget)} color="secondary" endIcon={<IoMdArrowDropdown />}>
                    Dalšie možnosti
                </Button>

                <Popover
                    open={!!anchorEl}
                    anchorEl={anchorEl}
                    onClose={() => setAnchorEl(null)}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                >
                    {actions.filter(action => action.visible).map((action, index) => {
                        if (!action.visible) return <></>

                        return <Box
                            key={index}
                            onClick={action.handler}
                            sx={{
                                paddingX: 2,
                                paddingY: 1.5,
                                cursor: "pointer",
                                ...getYCenterStyles(),
                                gap: 1,
                                transition: "all 0.3s ease",
                                "&:hover": {
                                    backgroundColor: theme.palette.grey[200],
                                }
                            }}
                        >
                            <action.icon color={action.color || theme.palette.grey[600]} size={24} />
                            <Typography variant="body2" color={action.color} >{action.label}</Typography>
                        </Box>
                    })}
                </Popover>
            </>
        )}

        {!moreOptionsVisible && (
            actions.filter(action => action.visible).map((action, index) => {
                if (!action.visible) return <></>

                return <Button
                    key={index}
                    size="large"
                    fullWidth={props.isPageForm && onlySmallScreen}
                    onClick={action.handler}
                    color="primary"
                    variant="contained"
                    startIcon={<action.icon />}
                >
                    {action.label}
                </Button>
            })
        )}

        {/* Show submit button when:
            - Request is sent for sign (regardless of isManuallySigned status), OR
            - Request is manually signed
            AND request is not yet submitted */}
        {showSubmitSentForSignOrSigned && submitButtonWithNotification}

        {showSubmitDraftFlow && !showSubmitSentForSignOrSigned && (
            <>
                {/* TODO NPM PACKAGE: IN NEW FIGMA THESE BUTTONS ARE DISPLAYED WE SHOULD DISCUSS IT IN FURTHER DISCUSSION */}
                {/* <LoadingButton
                    startIcon={<IoSave />}
                    color="success"
                    onClick={() => onlySaveRequest(false)}
                    disabled={displayLoaderInButtons}
                    fullWidth={props.isPageForm && onlySmallScreen}
                >
                    Uložiť žiadosť
                </LoadingButton> */}

                <Box
                    sx={{
                        display: "flex",
                        flexDirection: props.isPageForm && onlySmallScreen ? "column" : "row",
                        alignItems: props.isPageForm && onlySmallScreen ? "stretch" : "center",
                        gap: 2.5,
                        flexWrap: "wrap",
                        justifyContent: "flex-end",
                        width: props.isPageForm && onlySmallScreen ? "100%" : "auto",
                    }}
                >
                    {sendNotificationCheckbox}
                    <Tooltip
                        title="Polia označené hviezdičkou (*) sú povinné, musia byť vyplnené, aby sa mohla odoslať žiadosť"
                        disableHoverListener={!isRequestSubmitDisabled()}
                    >
                        <span style={{
                            display: props.isPageForm && onlySmallScreen ? "block" : "inline-flex",
                            width: props.isPageForm && onlySmallScreen ? "100%" : "auto",
                        }}>
                            {submitButton}
                        </span>
                    </Tooltip>
                </Box>
            </>
        )}

        {!props.isPageForm && closePopupButton}
    </Box>
}

export type RequestFormAlertProps = {
    severity: AlertProps["severity"];
    text: string | React.ReactNode;
}

const RequestFormAlert = (props: RequestFormAlertProps) => {
    const theme = useTheme();
    const severity = props.severity ?? "info";

    const iconColor =
        severity === "error"
            ? theme.palette.error.dark
            : severity === "warning"
                ? theme.palette.warning.dark
                : severity === "success"
                    ? theme.palette.success.dark
                    : theme.palette.info.dark;

    const alertIcon =
        severity === "error" ? (
            <ExclamationTriangleIcon width={16} height={16} color={iconColor} />
        ) : (
            <InfoIcon width={16} height={16} color={iconColor} />
        );

    return (
        <Alert
            severity={props.severity ?? "info"}
            icon={false}
            sx={{
                padding: "16px",
                ".MuiAlert-message": { padding: 0 },
                marginTop: "12px",
            }}
        >
            <Box sx={{ display: "flex", gap: 1 }}>
                <Box sx={{ marginTop: "6px" }}>{alertIcon}</Box>
                <Typography variant="subtitle1" color="text.primary" component="div" sx={{ flex: 1, width: "100%" }}>
                    {typeof props.text === "string" ? (
                        <Box
                            dangerouslySetInnerHTML={{
                                __html: props.text,
                            }}
                            sx={{ width: "100%" }}
                        />
                    ) : (
                        props.text
                    )}
                </Typography>
            </Box>
        </Alert>
    );
};
